/**
 * Mayhem challenges ("half-bricks"): a half-brick with a note tied round it, left somewhere in the
 * borough. Pick it up and the note says what to do and how long you have: two minutes to knock down the
 * Vanes' men, smash up their motors or send the pavement clutter flying, with the weapon or the car the
 * note leaves you. Every target scores, quick ones in a row score more (the combo), and the score is
 * stamped TIDY, PROPER or DIABOLICAL. Offline only. Mechanic inspired by timed score-attack challenges;
 * every name, place and line here is Borough Heat's own (docs/ACTIVITIES-OFFLINE.md).
 *
 * This file is the plain data and the pure maths (unit-tested): the challenges, the run's clock / score /
 * combo, the stamps, the pay and the saved record. `mayhemSystem.ts` plays them.
 */
import type { WeaponId } from '../weapons/weaponData';

/** What the challenge asks you to take apart. */
export type MayhemKind = 'heavies' | 'motors' | 'clutter';

export interface MayhemChallenge {
  /** `mh-…` (also the mission id the runner knows it by). */
  id: string;
  /** The headline on the card and the sheet. */
  title: string;
  /** Where the brick lies, for the book ("Bell Street, by the bookies"). */
  place: string;
  /** Road-graph spot (as the matchbooks): junction keys, a fraction along, a side. */
  a: string;
  b: string;
  t: number;
  side: 'L' | 'R';
  kind: MayhemKind;
  /** A weapon lent for the run (taken back after if you didn't own it). */
  weapon?: WeaponId;
  /** Spare rounds with a lent gun. */
  rounds?: number;
  /** A motor left at the kerb for the run (vehicle spec id). */
  car?: string;
  /** Targets to get. */
  count: number;
  /** Seconds on the clock. */
  seconds: number;
  /** What's written on the note (the intro card's blurb; voice, not instructions). */
  note: string;
}

/**
 * Ten bricks, two in each district, all away from the job givers and the matchbooks (unit-tested:
 * clear of colliders, ≥ 25 m from every POI and matchbook, ≥ 60 m apart).
 */
export const MAYHEM_CHALLENGES: readonly MayhemChallenge[] = [
  {
    id: 'mh-night-out',
    title: 'A Night Out',
    place: 'Bell Street, round the back of the Velvet Lamp',
    a: 'pa_b',
    b: 'bl_b',
    t: 0.5,
    side: 'R',
    kind: 'heavies',
    weapon: 'bat',
    count: 10,
    seconds: 120,
    note: 'Clive’s doormen have been throwing their weight about. Two minutes. Take the bat, and give them something to talk about.',
  },
  {
    id: 'mh-making-a-point',
    title: 'Making a Point',
    place: 'Ashby Street',
    a: 'as_m',
    b: 'as_w',
    t: 0.5,
    side: 'L',
    kind: 'motors',
    weapon: 'crowbar',
    count: 6,
    seconds: 120,
    note: 'The Vanes park their motors where they like. Here’s a crowbar. Make them think twice.',
  },
  {
    id: 'mh-settling-up',
    title: 'Settling Up',
    place: 'Furnace Street, Cinder Yards',
    a: 'w_b',
    b: 'cl_b',
    t: 0.55,
    side: 'R',
    kind: 'motors',
    car: 'van',
    count: 6,
    seconds: 120,
    note: 'Ronnie owes a man in the Yards. The van’s outside. Settle it with his motors.',
  },
  {
    id: 'mh-both-barrels',
    title: 'Both Barrels',
    place: 'Quay Street, by the bonded warehouses',
    a: 'qs_d',
    b: 'qs_bo',
    t: 0.35,
    side: 'L',
    kind: 'motors',
    weapon: 'sawnoff',
    rounds: 16,
    count: 7,
    seconds: 120,
    note: 'The Vanes’ motors are down the quay tonight. The shooter’s loaded. Don’t hang about.',
  },
  {
    id: 'mh-spring-clean',
    title: 'Spring Cleaning',
    place: 'Kessmouth Road, by the gasworks',
    a: 'gw_n',
    b: 'pa_n',
    t: 0.5,
    side: 'R',
    kind: 'clutter',
    car: 'coupe',
    count: 30,
    seconds: 100,
    note: 'Somebody wants the borough tidied up. The Vesper’s at the kerb. Bins, crates, bollards: everything goes.',
  },
  {
    id: 'mh-closing-time',
    title: 'Closing Time',
    place: 'Hob Lane',
    a: 'hl_n',
    b: 'hl_b',
    t: 0.5,
    side: 'L',
    kind: 'heavies',
    weapon: 'cosh',
    count: 12,
    seconds: 120,
    note: 'Vane boys drinking on our side of Coalgate. Here’s a cosh. Last orders, gentlemen.',
  },
  {
    id: 'mh-six-of-the-best',
    title: 'Six of the Best',
    place: 'Wharf Road, by the canal',
    a: 'tr_w',
    b: 'lk_w',
    t: 0.5,
    side: 'R',
    kind: 'motors',
    weapon: 'revolver',
    rounds: 24,
    count: 6,
    seconds: 120,
    note: 'Six of their motors on Wharf Road and six in the cylinder. Do the sums.',
  },
  {
    id: 'mh-round-two',
    title: 'Round Two',
    place: 'Eastgate, by the church',
    a: 'e_c',
    b: 'e_ch',
    t: 0.5,
    side: 'R',
    kind: 'heavies',
    weapon: 'fists',
    count: 8,
    seconds: 120,
    note: 'They jumped one of ours outside the Calypso. No tools this time. Put your fists up.',
  },
  {
    id: 'mh-bumper-cars',
    title: 'Bumper Cars',
    place: 'Dock Road',
    a: 'cl_d',
    b: 'pa_d',
    t: 0.4,
    side: 'L',
    kind: 'motors',
    car: 'compact',
    count: 7,
    seconds: 120,
    note: 'A Minnow, a full tank and the Vanes’ cars all along the Dock Road. Fairground rules.',
  },
  {
    id: 'mh-market-day',
    title: 'Market Day',
    place: 'the Ropewalk',
    a: 'pa_r',
    b: 'qs_r',
    t: 0.5,
    side: 'R',
    kind: 'clutter',
    car: 'van',
    count: 35,
    seconds: 100,
    note: 'The Ropewalk’s too neat by half. The Haulmaster’s at the kerb. Leave nothing standing.',
  },
];

export const MAYHEM_IDS: readonly string[] = MAYHEM_CHALLENGES.map((c) => c.id);

export function mayhemChallenge(id: string): MayhemChallenge | undefined {
  return MAYHEM_CHALLENGES.find((c) => c.id === id);
}

// ------------------------------------------------------------------ the run: clock, score, combo

/** Points for each target. */
export const TARGET_POINTS = 100;
/** The combo tops out at ×5. */
export const COMBO_MAX = 5;
/** Seconds after a hit inside which the next target raises the combo, by kind. */
export const COMBO_WINDOW: Record<MayhemKind, number> = { heavies: 3.5, motors: 4.5, clutter: 2.5 };
/**
 * Finishing early: the share of the clock left (whole seconds) times this share of the run's base score
 * (`count × TARGET_POINTS`). Capped well under the combo's worth, so speed helps but only quick hits in a
 * row reach DIABOLICAL.
 */
export const TIME_BONUS_SHARE = 0.6;
/** The stamps, lowest first. */
export const TIER_NAMES = ['Tidy', 'Proper', 'Diabolical'] as const;
/** Score thresholds for the stamps, as fractions of `count × TARGET_POINTS`. */
export const TIER_FRACTIONS = [0.6, 1.5, 2.6] as const;
/** Pay for each stamp (modest: a street job pays about £150–£300). */
export const TIER_PAY = [60, 120, 200] as const;
/** Paid once per challenge, the first time it is stamped DIABOLICAL. */
export const FIRST_DIABOLICAL_BONUS = 100;
/** The failed sheet's stamp. */
export const MAYHEM_FAIL_STAMP = 'ALL MOUTH';

/** The bonus for finishing with `left` seconds of `seconds` on the clock. */
export function timeBonus(left: number, seconds: number, count: number): number {
  return Math.round((Math.floor(Math.max(0, left)) / Math.max(1, seconds)) * TIME_BONUS_SHARE * count * TARGET_POINTS);
}

export function tierThresholds(count: number): number[] {
  return TIER_FRACTIONS.map((f) => Math.round(count * TARGET_POINTS * f));
}

/** 0 = no stamp, 1..3 = Tidy / Proper / Diabolical. */
export function tierFor(score: number, count: number): number {
  const t = tierThresholds(count);
  let tier = 0;
  for (let i = 0; i < t.length; i++) if (score >= t[i]!) tier = i + 1;
  return tier;
}

export function tierName(tier: number): string {
  return tier >= 1 ? TIER_NAMES[Math.min(3, tier) - 1]! : 'No stamp';
}

/** What a hit did: the points and the combo they were scored at. */
export interface MayhemHit {
  points: number;
  combo: number;
  /** A new stamp was reached with this hit (1..3), else 0. */
  tierUp: number;
}

/**
 * One run of a challenge: the clock, the targets got, the score and the combo. Plain maths, stepped by
 * the system (or a test) with `tick` and fed `hit` / `graze`.
 */
export class MayhemRun {
  left: number;
  done = 0;
  score = 0;
  combo = 1;
  bestCombo = 1;
  /** Seconds since the last hit or graze (the combo dies when this passes the window). */
  sinceHit = Infinity;
  /** The time bonus, once every target is down. */
  timeBonus = 0;
  over = false;
  /** Stamp reached so far. */
  tier = 0;

  constructor(
    readonly count: number,
    readonly seconds: number,
    readonly window: number,
  ) {
    this.left = seconds;
  }

  /** Seconds the run has been going. */
  get elapsed(): number {
    return this.seconds - Math.max(0, this.left);
  }

  /** The combo the next target would score at (×1 once the window has passed). */
  get liveCombo(): number {
    return this.sinceHit <= this.window ? this.combo : 1;
  }

  tick(dt: number): void {
    if (this.over) return;
    this.left = Math.max(0, this.left - dt);
    this.sinceHit += dt;
    if (this.sinceHit > this.window) this.combo = 1;
    if (this.left <= 0) this.over = true;
  }

  /** A target went down. Null once the run is over. */
  hit(): MayhemHit | null {
    if (this.over) return null;
    this.combo = this.sinceHit <= this.window ? Math.min(COMBO_MAX, this.combo + 1) : 1;
    this.bestCombo = Math.max(this.bestCombo, this.combo);
    this.sinceHit = 0;
    this.done++;
    const points = TARGET_POINTS * this.combo;
    this.score += points;
    if (this.done >= this.count) {
      this.timeBonus = timeBonus(this.left, this.seconds, this.count);
      this.score += this.timeBonus;
      this.over = true;
    }
    const before = this.tier;
    this.tier = tierFor(this.score, this.count);
    return { points, combo: this.combo, tierUp: this.tier > before ? this.tier : 0 };
  }

  /** A blow that didn't finish a target (a dent in a motor): keeps a running combo alive, adds nothing. */
  graze(): void {
    if (this.over || this.sinceHit > this.window) return;
    this.sinceHit = 0;
  }
}

// ------------------------------------------------------------------ pay and the saved record

export interface MayhemRecord {
  /** Best score per challenge id. */
  best: Record<string, number>;
  /** Best stamp per challenge id (1..3). */
  tier: Record<string, number>;
  /** Runs played (any result). */
  runs: number;
}

export function emptyMayhem(): MayhemRecord {
  return { best: {}, tier: {}, runs: 0 };
}

/** The tally lines for a stamped run (nothing for an unstamped one: that's a failed sheet). */
export function mayhemPayout(tier: number, previousTier: number): { label: string; amount: number }[] {
  if (tier < 1) return [];
  const lines: { label: string; amount: number }[] = [{ label: `Stamped ${tierName(tier).toUpperCase()}`, amount: TIER_PAY[tier - 1]! }];
  if (tier >= 3 && previousTier < 3) lines.push({ label: 'First time DIABOLICAL', amount: FIRST_DIABOLICAL_BONUS });
  return lines;
}

/** Book a finished run into the record. Returns whether the score was a new best. */
export function bookRun(rec: MayhemRecord, id: string, score: number, tier: number): boolean {
  rec.runs += 1;
  const s = Math.max(0, Math.round(score));
  const prev = rec.best[id] ?? 0;
  if (tier > (rec.tier[id] ?? 0)) rec.tier[id] = tier;
  if (s > prev) {
    rec.best[id] = s;
    return true;
  }
  return false;
}

type Json = Record<string, unknown>;
const isObj = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);
const ID_RE = /^mh-[a-z0-9-]{1,36}$/;

/** Clamp and default every field (old saves have no record at all). Never throws. */
export function sanitizeMayhem(raw: unknown): MayhemRecord {
  const d = emptyMayhem();
  if (!isObj(raw)) return d;
  const table = (v: unknown, hi: number, round: (n: number) => number): Record<string, number> => {
    const out: Record<string, number> = {};
    if (!isObj(v)) return out;
    for (const [k, n] of Object.entries(v).slice(0, 64)) {
      if (!ID_RE.test(k) || typeof n !== 'number' || !Number.isFinite(n)) continue;
      const x = round(Math.min(hi, Math.max(0, n)));
      if (x > 0) out[k] = x;
    }
    return out;
  };
  return {
    best: table(raw.best, 9_999_999, Math.round),
    tier: table(raw.tier, 3, Math.round),
    runs: typeof raw.runs === 'number' && Number.isFinite(raw.runs) ? Math.round(Math.min(1e7, Math.max(0, raw.runs))) : 0,
  };
}

// ------------------------------------------------------------------ words

/** The objective: what to do (plain; no controls named). The count and the clock are on the slip. */
export function mayhemObjective(c: MayhemChallenge): string {
  switch (c.kind) {
    case 'heavies':
      return `Knock down ${c.count} of the Vanes’ men, marked in red, before the clock runs out.`;
    case 'motors':
      return `Smash up ${c.count} of the Vanes’ claret motors before the clock runs out.`;
    default:
      return `Knock over ${c.count} bins, crates, cones or bollards before the clock runs out.`;
  }
}

/** The hint: how (names the control for the device). */
export function mayhemHint(c: MayhemChallenge, touch: boolean): string {
  const swing = touch ? 'HIT' : 'Q or left click';
  const quick = 'Quick ones in a row score more.';
  if (c.car) return c.kind === 'clutter' ? `Drive along the pavements in the ${carName(c.car)}. ${quick}` : `Ram them with the ${carName(c.car)} until they’re wrecked. ${quick}`;
  if (c.weapon === 'revolver' || c.weapon === 'sawnoff') return `Aim (${touch ? 'AIM' : 'K or right mouse'}) and fire (${swing}) until each one is wrecked. ${quick}`;
  if (c.weapon === 'fists') return `Punch them (${swing}). It takes a few each. ${quick}`;
  return c.kind === 'motors' ? `Hit them with the ${weaponName(c.weapon)} (${swing}) until each one is wrecked. ${quick}` : `Hit them with the ${weaponName(c.weapon)} (${swing}). ${quick}`;
}

export function carName(spec: string): string {
  return spec === 'van' ? 'van' : spec === 'coupe' ? 'Vesper' : spec === 'compact' ? 'Minnow' : spec === 'saloon' ? 'Regent' : spec === 'estate' ? 'Drover' : spec === 'exec' ? 'Grandee' : spec === 'roadster' ? 'Linnet' : 'car';
}

export function weaponName(w: WeaponId | undefined): string {
  switch (w) {
    case 'bat':
      return 'bat';
    case 'crowbar':
      return 'crowbar';
    case 'cosh':
      return 'cosh';
    case 'revolver':
      return 'revolver';
    case 'sawnoff':
      return 'sawn-off';
    default:
      return 'fists';
  }
}

/** The card's small label: "HALF-BRICK · 2 MIN · BAT". */
export function mayhemTag(c: MayhemChallenge): string {
  const what = c.car ? carName(c.car) : weaponName(c.weapon);
  const mins = c.seconds % 60 === 0 ? `${c.seconds / 60} MIN` : `${Math.floor(c.seconds / 60)}:${String(c.seconds % 60).padStart(2, '0')}`;
  return `HALF-BRICK · ${mins} · ${what.toUpperCase()}`;
}

export function formatScore(n: number): string {
  return Math.round(n).toLocaleString('en-GB');
}
